import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import Home from "@/pages/Home";
import Library from "@/pages/Library";
import ReportDetail from "@/pages/ReportDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminReportEdit from "@/pages/AdminReportEdit";
import AdminInquiries from "@/pages/AdminInquiries";
import About from "@/pages/About";
import Aircraft from "@/pages/Aircraft";
import Disclosures from "@/pages/Disclosures";
import ProfessionalPilots from "@/pages/ProfessionalPilots";
import FBOServices from "@/pages/FBOServices";
import AircraftMaintenance from "@/pages/AircraftMaintenance";
import VestibuleGate from "@/components/VestibuleGate";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPath = useRef(pathname);
  useEffect(() => {
    // Safety net: a real page navigation must never inherit a stuck scroll-lock
    // from a modal on the previous page. The vestibule and the Two Doors overlay
    // are page-scoped and closed on navigation, so on any pathname CHANGE we
    // force-clear the background scroll-lock styles. (Skipped on first load /
    // hash-only changes so the vestibule, which legitimately locks scroll on the
    // landing page, isn't unlocked.)
    if (prevPath.current !== pathname) {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    prevPath.current = pathname;

    // With a hash (e.g. /about#inquiry), scroll to that section once it has
    // mounted — offset for the fixed 88px navbar — instead of jumping to top.
    if (hash) {
      const id = hash.replace(/^#/, "");
      let raf = 0;
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          const y = Math.max(el.getBoundingClientRect().top + window.scrollY - 88, 0);
          window.scrollTo({ top: y, behavior: "smooth" });
          return;
        }
        if (tries++ < 60) raf = requestAnimationFrame(tryScroll);
      };
      raf = requestAnimationFrame(tryScroll);
      return () => cancelAnimationFrame(raf);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
}

const RequireAuth = ({ children }) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("steck_admin_token") : null;
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <VestibuleGate>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/operating-outlook" element={<Disclosures />} />
            <Route path="/important-facts" element={<Disclosures />} />
            <Route path="/who-we-are" element={<Disclosures />} />
            <Route path="/disclosures" element={<Disclosures />} />
            <Route path="/insights" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/aircraft/beechjet-400a" element={<Aircraft />} />
            <Route path="/fbo-services" element={<FBOServices />} />
            <Route path="/aircraft-maintenance" element={<AircraftMaintenance />} />
            <Route path="/library" element={<Library />} />
            <Route path="/research/professional-pilots" element={<ProfessionalPilots />} />
            <Route path="/reports/:slug" element={<ReportDetail />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
            <Route path="/admin/inquiries" element={<RequireAuth><AdminInquiries /></RequireAuth>} />
            <Route path="/admin/reports/new" element={<RequireAuth><AdminReportEdit /></RequireAuth>} />
            <Route path="/admin/reports/:id/edit" element={<RequireAuth><AdminReportEdit /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </VestibuleGate>
      </BrowserRouter>
    </div>
  );
}

export default App;
